import type { LocalDataColumn } from "@nymbl/nos2-rules";
import { MY_REQUESTS_HELP } from "@/lib/column-help";
import type { PtoStatus } from "@/lib/pto/approval-rules";
import { typeLabel, workingDates } from "@/lib/pto/days";
import type { IsoDate } from "@/lib/time/dates";

/** The PTO page's "My requests" list (spec §1.3, §1.5) from `ed-pto-my-requests`. */

export interface MyRequest {
  id: string;
  typeId: string;
  typeEnum: string;
  typeName: string;
  startDate: IsoDate;
  endDate: IsoDate;
  hoursPerDay: number;
  description: string | null;
  rejectionNote: string | null;
  deleteNote: string | null;
  status: PtoStatus;
  approverId: string | null;
  approverName: string | null;
  workDays: number;
  days: number;
  bookedDays: number;
  hasLockedEntries: boolean;
  touchesClosedMonth: boolean;
  changedOn: string | null;
  /** Filed by the person (AddedBy is their email); false for holidays HR bulk-loaded. */
  addedBySelf: boolean;
}

export const STATUS_LABELS: Record<PtoStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  requestdelete: "Cancel asked",
  indraft: "Draft",
};

export type RequestAction = "edit" | "cancelRequest" | "delete" | "cancelTimeOff" | "askCancel";

/** What the requester may do with a row (D2, D3). The gateway re-checks every one. */
export function requestActions(r: MyRequest, today: IsoDate): RequestAction[] {
  // Company holidays HR booked for the person: nothing to edit, withdraw or cancel. Holiday
  // requests the person filed (birthday, non-US holidays; D6) get the normal actions below.
  if (r.typeEnum === "holiday" && !r.addedBySelf) return [];
  // Locked entries mean booked days (no edit) the gateway will not delete (old data only).
  if (r.status !== "approved" && r.hasLockedEntries) return [];
  if (r.status === "pending") return ["edit", "cancelRequest"];
  if (r.status === "indraft" || r.status === "rejected") return ["edit", "delete"];
  if (r.status !== "approved" || r.touchesClosedMonth) return [];
  if (r.startDate > today) return r.hasLockedEntries ? [] : ["cancelTimeOff"];
  return ["askCancel"];
}

/** The toast after a delete: an open request is withdrawn, approved time off is cancelled. */
export function deleteToast(status: PtoStatus): string {
  return status === "approved" ? "Cancelled" : "Withdrawn";
}

/** Booked days once an approved request has entries; the request's own count otherwise. */
export function shownDays(r: MyRequest): number {
  return r.status === "approved" && r.bookedDays > 0 ? r.bookedDays : r.days;
}

export type MyRequestView = "upcoming" | "past";

export function viewOf(r: MyRequest, today: IsoDate): MyRequestView {
  return r.endDate >= today ? "upcoming" : "past";
}

/**
 * The upcoming/past view and the holidays toggle; search and column filters belong to the table kit.
 * The toggle hides booked (approved) holidays only, so a holiday request still waiting on someone
 * never drops out of view.
 */
export function filterMyRequests(
  rows: MyRequest[],
  view: MyRequestView,
  today: IsoDate,
  showHolidays: boolean,
): MyRequest[] {
  return rows
    .filter((r) => viewOf(r, today) === view)
    .filter((r) => showHolidays || r.typeEnum !== "holiday" || r.status !== "approved");
}

/**
 * The order before any header sort: rejected first, then start date (past: newest first).
 * Pass as the kit's `tieBreak`, or sort the rows with it before `applyTableState`.
 */
export function defaultRequestOrder(view: MyRequestView): (a: MyRequest, b: MyRequest) => number {
  return (a, b) =>
    Number(b.status === "rejected") - Number(a.status === "rejected") ||
    (view === "past"
      ? b.startDate.localeCompare(a.startDate)
      : a.startDate.localeCompare(b.startDate)) ||
    a.id.localeCompare(b.id);
}

/** Column declarations for the shared table kit (sort, filter and search all come from it). */
export const MY_REQUEST_COLUMNS: LocalDataColumn<MyRequest>[] = [
  {
    key: "dates",
    label: "Dates",
    type: "date",
    accessor: (r) => r.startDate,
    help: MY_REQUESTS_HELP.dates,
  },
  {
    key: "type",
    label: "Type",
    type: "enum",
    accessor: (r) => typeLabel(r.typeEnum, r.typeName),
    help: MY_REQUESTS_HELP.type,
  },
  {
    key: "days",
    label: "Days",
    type: "number",
    accessor: (r) => shownDays(r),
    help: MY_REQUESTS_HELP.days,
  },
  {
    key: "status",
    label: "Status",
    type: "enum",
    accessor: (r) => r.status,
    options: Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
    help: MY_REQUESTS_HELP.status,
  },
  {
    key: "note",
    label: "Note",
    type: "text",
    accessor: (r) => r.rejectionNote ?? r.description,
    searchText: (r) => [r.description, r.rejectionNote].filter(Boolean).join(" "),
    help: MY_REQUESTS_HELP.note,
  },
  {
    key: "approver",
    label: "Approver",
    type: "text",
    accessor: (r) => r.approverName,
    help: MY_REQUESTS_HELP.approver,
  },
];

/** Dates of the person's approved holiday-type requests (picker dots; spec R1 preview). */
export function ownHolidayDates(rows: MyRequest[]): Set<IsoDate> {
  const out = new Set<IsoDate>();
  for (const r of rows) {
    if (r.typeEnum !== "holiday" || r.status !== "approved") continue;
    for (const d of workingDates(r.startDate, r.endDate, new Set())) out.add(d);
  }
  return out;
}
